import { useUpdateUser } from '@/hooks/api/useUsers'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { useQueryClient } from '@tanstack/react-query'
import { useToast } from '@/components/shared/toast/Toast'
import { useUserPreferences } from '@/stores/userPreferences'
import type { ProfileDto } from '@wtf/shared'

export function useUpdateCurrency() {
    const { user } = useCurrentUser()
    const updateUserMutation = useUpdateUser()
    const queryClient = useQueryClient()
    const { addToast } = useToast()
    const { setPreferredCurrency, preferredCurrency } = useUserPreferences()

    const updateCurrency = async (
        code: string,
        options?: {
            showSuccessToast?: boolean
            onSuccess?: () => void
        }
    ) => {
        if (!user?.uid) {
            // If not authenticated, just update local preference store
            setPreferredCurrency(code)
            return
        }

        // Don't update if selecting the same currency
        if (code === preferredCurrency) {
            return
        }

        // Immediately update local state for instant UI feedback
        setPreferredCurrency(code)

        // Optimistically update the UI
        queryClient.setQueryData<ProfileDto>(
            ['user', 'profile', user.uid],
            (old) => {
                if (!old) return old
                return { ...old, preferredCurrency: code }
            }
        )

        try {
            await updateUserMutation.mutateAsync({
                id: user.uid,
                data: { preferredCurrency: code },
            })

            // Invalidate queries to ensure fresh data
            queryClient.invalidateQueries({
                queryKey: ['user', 'profile', user.uid],
            })

            if (options?.showSuccessToast) {
                addToast('Currency updated successfully', 'success')
            }

            options?.onSuccess?.()
        } catch (err) {
            console.error('Error updating currency:', err)

            // Revert local state on error
            const previousCurrency =
                queryClient.getQueryData<ProfileDto>([
                    'user',
                    'profile',
                    user.uid,
                ])?.preferredCurrency || 'USD'
            setPreferredCurrency(previousCurrency)

            // Revert optimistic update on error
            queryClient.invalidateQueries({
                queryKey: ['user', 'profile', user.uid],
            })

            addToast('Failed to update currency', 'error')
        }
    }

    return { updateCurrency }
}
